import { C } from '../data/constants.js';
import { PU_TYPES, PU_CONFIG } from '../entities/PowerUp.js';

export class PowerUpManager {
  constructor() {
    // Active effects: Map<type, { timer: ms remaining, ... }>
    this.activeEffects = new Map();
    
    // Max simultaneous timed power-ups
    this.MAX_ACTIVE = 3;
    
    // Shield active flag
    this.shieldActive = false;
    
    // Fireball active flag
    this.fireballActive = false;
    
    // Magnet catch active flag and remaining catches count (3 catches)
    this.magnetActive = false;
    this.magnetCatches = 0;
    
    // Wide paddle multiplier
    this.paddleWidthMultiplier = 1.0;
    
    // Laser active flag
    this.laserActive = false;
    
    // Speed multiplier for ball
    this.ballSpeedMultiplier = 1.0;
  }

  activate(type) {
    switch (type) {
      case PU_TYPES.MULTI_BALL:
        // Instant — handled by Game.js directly
        return { action: 'multiball' };
      
      case PU_TYPES.FIREBALL:
        this._setTimedEffect(type, 8000);
        this.fireballActive = true;
        return { action: 'fireball' };
      
      case PU_TYPES.WIDE_PADDLE:
        this._setTimedEffect(type, 12000);
        this.paddleWidthMultiplier = 1.6;
        return { action: 'wide_paddle' };
      
      case PU_TYPES.LASER:
        this._setTimedEffect(type, 10000);
        this.laserActive = true;
        return { action: 'laser' };
      
      case PU_TYPES.MAGNET:
        this.magnetCatches = 3;
        this.magnetActive = true;
        return { action: 'magnet' };
      
      case PU_TYPES.EMP:
        // Instant — handled by Game.js directly
        return { action: 'emp' };
      
      case PU_TYPES.SHIELD:
        this.shieldActive = true;
        return { action: 'shield' };
      
      case PU_TYPES.SPEED_DOWN:
        this._setTimedEffect(type, 10000);
        this.ballSpeedMultiplier = 0.7;
        return { action: 'speed_down' };
    }
    return null;
  }

  _setTimedEffect(type, durationMs) {
    // If already active, just refresh the timer
    if (this.activeEffects.has(type)) {
      this.activeEffects.get(type).timer = durationMs;
      return;
    }
    
    // Enforce max active limit — expire oldest if needed
    if (this.activeEffects.size >= this.MAX_ACTIVE) {
      const oldest = this.activeEffects.keys().next().value;
      this._expireEffect(oldest);
    }
    
    this.activeEffects.set(type, {
      timer: durationMs,
      maxTimer: durationMs,
    });
  }

  _expireEffect(type) {
    this.activeEffects.delete(type);
    
    switch (type) {
      case PU_TYPES.FIREBALL:
        this.fireballActive = false;
        break;
      case PU_TYPES.WIDE_PADDLE:
        this.paddleWidthMultiplier = 1.0;
        break;
      case PU_TYPES.LASER:
        this.laserActive = false;
        break;
      case PU_TYPES.SPEED_DOWN:
        this.ballSpeedMultiplier = 1.0;
        break;
    }
  }

  update(dtMs) {
    for (const [type, effect] of this.activeEffects) {
      effect.timer -= dtMs;
      if (effect.timer <= 0) {
        this._expireEffect(type);
      }
    }
  }

  // Render active power-up indicators at bottom of screen
  renderIndicators(ctx) {
    let x = 8;
    const y = C.CANVAS_H - 16;
    
    for (const [type, effect] of this.activeEffects) {
      const config = PU_CONFIG[type];
      const progress = effect.timer / effect.maxTimer;
      
      // Background pill
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.beginPath();
      ctx.roundRect(x, y - 6, 50, 14, 3);
      ctx.fill();
      
      // Progress bar
      ctx.fillStyle = config.color;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.roundRect(x, y - 6, 50 * progress, 14, 3);
      ctx.fill();
      ctx.globalAlpha = 1;
      
      // Label
      ctx.fillStyle = '#ffffff';
      ctx.font = '8px monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${config.icon} ${Math.ceil(effect.timer / 1000)}s`, x + 3, y + 1);
      
      x += 56;
    }
    
    // Shield indicator
    if (this.shieldActive) {
      ctx.fillStyle = '#ff8800';
      ctx.font = '8px monospace';
      ctx.textAlign = 'left';
      ctx.fillText('\u{1F6E1} SHIELD', x, y + 1);
    }
    
    // Magnet indicator
    if (this.magnetCatches > 0) {
      ctx.fillStyle = '#cc44ff';
      ctx.font = 'bold 8px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`\u{1F9F2} MAGNET (${this.magnetCatches})`, x + (this.shieldActive ? 55 : 0), y + 1);
    }
  }

  useMagnetCatch() {
    if (this.magnetCatches > 0) {
      this.magnetCatches--;
      if (this.magnetCatches <= 0) {
        this.magnetActive = false;
      }
    }
  }

  // Render shield barrier line at bottom of screen (if active)
  renderShield(ctx) {
    if (!this.shieldActive) return;
    
    ctx.strokeStyle = '#ff8800';
    ctx.shadowColor = '#ff8800';
    ctx.shadowBlur = 6;
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.beginPath();
    ctx.moveTo(C.BORDER_WIDTH, C.CANVAS_H - 5);
    ctx.lineTo(C.CANVAS_W - C.BORDER_WIDTH, C.CANVAS_H - 5);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.shadowBlur = 0;
  }

  reset() {
    this.activeEffects.clear();
    this.shieldActive = false;
    this.fireballActive = false;
    this.magnetActive = false;
    this.magnetCatches = 0;
    this.paddleWidthMultiplier = 1.0;
    this.laserActive = false;
    this.ballSpeedMultiplier = 1.0;
  }
}
